import React, {useState, useEffect} from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Divider,
    Stack,
} from '@mui/material';
import {UtilityForm} from "../utils/UtilityForm";
import dayjs from "dayjs";
import {Log} from '../utils/Log';
import {useLocale} from "../contexts/LocaleContext";
import {useTranslation} from "react-i18next";
import {UserAPI} from "../services/API/UserAPI";
import {UserDTO} from "../types/DTOs/UserDTO";
import {Mail, Shield, CalendarToday} from '@mui/icons-material';

const UserProfile: React.FC = () => {
    const [userData, setUserData] = useState<UserDTO>({
        userName: '',
        email: '',
        password: '',
        lastLogin: null,
        isAuthOnGoogle: false,
        createdAt: null,
    });

    const setUserDataDefault = () => {
        setUserData({
            userName: '',
            email: '',
            password: '',
            lastLogin: null,
            isAuthOnGoogle: false,
            createdAt: null,
        });
    };

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [passwordEditMode, setPasswordEditMode] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const {locale} = useLocale();
    const {t} = useTranslation();

    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async () => {
        try {
            setLoading(true);
            const data = await UserAPI.Instance<UserAPI>().getUserData();
            setUserData(data);
            Log.writeLogDebug('Oggetto caricato');
            //Log.writeLogDebug(userData);
        } catch (error) {
            console.error('Error loading user data:', error);

            if (error instanceof Error && 'response' in error) {
                const httpError = error as any;
                if (httpError.response?.status === 404) {
                    setUserDataDefault();
                } else {
                    setError(t('standardUse.errorLoad'));
                }
            } else {
                setError(t('standardUse.errorLoad'));
            }
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async () => {
        Log.writeLogDebug('Inizio salvataggio');
        Log.writeLogDebug(userData);

        if (passwordEditMode && userData.password !== confirmPassword) {
            setError(t('userProfile.passwordMismatch'));
            return;
        }

        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const payload: any = {
                userName: userData.userName,
                email: userData.email,
                password: userData.password || '',
            };
            await UserAPI.Instance<UserAPI>().updateUserData(payload);
            setSuccess(t('standardUse.updateOk'));
            setPasswordEditMode(false);
            setConfirmPassword('');
        } catch (err: any) {
            const status = err.response?.status;
            const msg = err.response?.data?.message;
            if (status === 409) {
                setError(t('userProfile.emailConflict'));
            } else {
                setError(msg || t('standardUse.errorSave'));
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{mt: 4}}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{mt: 4, mb: 4}}>
            <Typography variant="h4" component="h1" gutterBottom>
                {t('userProfile.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{mb: 4}}>
                {t('userProfile.descriptionPage')}
            </Typography>

            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{mb: 2}}>
                    {success}
                </Alert>
            )}



            <Card>
                <CardContent>
                    <Box component="form">
                        <Grid container spacing={3}>
                            {userData.isAuthOnGoogle && (
                                <Grid size={{xs: 12}}>
                                    <Alert severity="info" icon={<Shield/>}>
                                        {t('userProfile.googleAccountConnected')}
                                    </Alert>
                                </Grid>
                            )}

                            <Grid size={{xs: 12, sm: 6}}>
                                <TextField
                                    fullWidth
                                    name="userName"
                                    label={t('userProfile.userName')}
                                    value={userData.userName}
                                    onChange={(e) => UtilityForm.handleInputChange(e, setUserData)}
                                />
                            </Grid>

                            <Grid size={{xs: 12, sm: 6}}>
                                <TextField
                                    fullWidth
                                    name="email"
                                    label={t('userProfile.email')}
                                    type="email"
                                    value={userData.email}
                                    disabled={userData.isAuthOnGoogle}
                                    onChange={(e) => UtilityForm.handleInputChange(e, setUserData)}
                                    helperText={userData.isAuthOnGoogle ? t('userProfile.emailGoogleHelp') : ''}
                                />
                            </Grid>

                            {!userData.isAuthOnGoogle && (
                                <>
                                    <Grid size={{xs: 12, sm: 6}}>
                                        <TextField
                                            fullWidth
                                            name="password"
                                            label={t('userProfile.password')}
                                            type="password"
                                            value={userData.password || ''}
                                            disabled={!passwordEditMode}
                                            onChange={(e) => UtilityForm.handleInputChange(e, setUserData)}
                                            placeholder={t('userProfile.passwordPlaceholder')}
                                        />
                                    </Grid>
                                    {passwordEditMode && (
                                        <Grid size={{xs: 12, sm: 6}}>
                                            <TextField
                                                fullWidth
                                                label={t('userProfile.confirmPassword')}
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                error={!!confirmPassword && userData.password !== confirmPassword}
                                                helperText={
                                                    confirmPassword && userData.password !== confirmPassword
                                                        ? t('userProfile.passwordMismatch')
                                                        : ''
                                                }
                                            />
                                        </Grid>
                                    )}
                                    <Grid size={{xs: 12}}>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                if (passwordEditMode) {
                                                    setPasswordEditMode(false);
                                                    setConfirmPassword('');
                                                } else {
                                                    setPasswordEditMode(true);
                                                }
                                            }}
                                        >
                                            {passwordEditMode
                                                ? t('userProfile.cancelPasswordChange')
                                                : t('userProfile.changePassword')}
                                        </Button>
                                    </Grid>
                                </>
                            )}

                            <Grid size={{xs: 12}}>
                                <Divider sx={{my: 2}}/>
                            </Grid>

                            <Grid size={{xs: 12}}>
                                <Box sx={{borderRadius: 2, p: 3}}>
                                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                        {t('userProfile.accountInfo')}
                                    </Typography>

                                    <Stack spacing={2} sx={{mt: 2}}>
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                <CalendarToday fontSize="small" color="action"/>
                                                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                                    {t('userProfile.lastLogin')}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body1" sx={{pl: 4}}>
                                                {userData.lastLogin
                                                    ? dayjs(userData.lastLogin).format('DD/MM/YYYY HH:mm:ss')
                                                    : t('standardUse.noData')}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                <CalendarToday fontSize="small" color="action"/>
                                                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                                    {t('userProfile.createdAt')}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body1" sx={{pl: 4}}>
                                                {userData.createdAt
                                                    ? dayjs(userData.createdAt).format('DD/MM/YYYY HH:mm:ss')
                                                    : t('standardUse.noData')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{my: 3}}/>

                        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                            <Button
                                variant="outlined"
                                disabled={saving}
                                onClick={loadUserData}
                            >
                                {t('standardUse.undoBtn')}
                            </Button>
                            <Button onClick={onSubmit} variant="contained" disabled={saving}>
                                {saving ? <CircularProgress size={24}/> : t('standardUse.saveBtn')}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
};

export default UserProfile;